/**
 * A QR code of some text, drawn as one SVG path (what an authenticator app scans to learn a
 * second step's secret). Drawn from the code's squares, never from HTML, so nothing in the
 * text can become markup.
 */
import qrcode from "qrcode-generator";
import type { JSX } from "solid-js";

/** The squares of `text`'s QR code as an SVG path, and how many squares a side has (quiet zone included). */
export function qrPath(text: string): { path: string; size: number } {
  const code = qrcode(0, "M");
  code.addData(text);
  code.make();
  const count = code.getModuleCount();
  const margin = 4;
  let path = "";
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (code.isDark(row, col)) path += `M${col + margin} ${row + margin}h1v1h-1z`;
    }
  }
  return { path, size: count + margin * 2 };
}

/** The QR code of `text`, `pixels` wide, dark on white (scanners want that, whatever the theme). */
export function QrCode(props: { text: string; pixels?: number; id?: string }): JSX.Element {
  const drawn = () => qrPath(props.text);
  return (
    <svg
      id={props.id}
      class="qr-code"
      width={props.pixels ?? 192}
      height={props.pixels ?? 192}
      viewBox={`0 0 ${drawn().size} ${drawn().size}`}
      shape-rendering="crispEdges"
      role="img"
      aria-label="QR code to scan with an authenticator app"
    >
      <rect width={drawn().size} height={drawn().size} fill="#fff" />
      <path d={drawn().path} fill="#000" />
    </svg>
  );
}
